(ns ui.mcp.page
  "The Storrito MCP page: how to connect Claude to the organization's
   MCP server. The Claude screens are mockups (`ui.mcp.mockups`) instead
   of screenshots, so they carry the organization's real MCP URL and a
   change in Claude.ai is a small diff. The content lives in
   `ui.mcp.guide`, this namespace is the layout."
  (:require [storrito.ui.core :as ui]
            [ui.app-frame :as app-frame]
            [ui.elements :as elements]
            [ui.mcp.guide :as guide]
            [ui.mcp.hint :as hint]))

(def copy-button-class
  ["inline-flex" "items-center" "shrink-0" "px-3" "py-1.5" "text-xs"
   "font-medium" "text-gray-700" "bg-gray-100" "rounded" "hover:bg-gray-200"
   "cursor-pointer" "whitespace-nowrap"])

(defn copy-button
  "Copies `text` to the clipboard and shows `Copied!` for a moment, see
   the `:clipboard/copy` action in `ui.action-handlers`."
  [state text store-key]
  [:button {:class copy-button-class
            :on {:click [[:clipboard/copy text store-key]]}}
   (if (get state store-key) "Copied!" "Copy")])

(defn code-with-copy
  [state text store-key]
  [:div {:class ["flex" "items-center" "gap-3" "rounded-lg" "border"
                 "border-gray-200" "bg-gray-50" "px-3" "py-2"]}
   [:code {:class ["min-w-0" "flex-1" "break-all" "text-sm" "text-gray-800"]}
    text]
   (copy-button state text store-key)])

(defn copyable-input
  "A field of a mockup as real UI: a readonly input plus Copy button,
   because readers copy the value from the spot where Claude shows it.
   The mockup frame switches pointer events and text selection off, this
   element switches them back on."
  [state {:keys [value label store-key]}]
  [:div {:class ["flex" "min-w-0" "flex-1" "items-center" "gap-2"
                 "pointer-events-auto" "select-text"]}
   [:input {:type "text"
            :readOnly true
            :value value
            :aria-label label
            :class ["min-w-0" "flex-1" "border-0" "bg-transparent" "p-0"
                    "text-sm" "font-medium" "text-stone-800" "focus:ring-0"]}]
   (copy-button state value store-key)])

(defn url-card
  [state mcp-url]
  [:div {:class (into ["mt-6" "border" "border-gray-200"] elements/panel)}
   [:h2 {:class elements/h3} "Your MCP server URL"]
   [:p {:class (into ["mt-1" "mb-3"] elements/p-sm)}
    "This URL belongs to your organization. Paste it into Claude in step 2."]
   (code-with-copy state mcp-url :mcp-page/url-copied)])

(defn section
  [title & children]
  (into [:section {:class ["mt-12"]}
         [:h2 {:class (into ["mb-4"] elements/h2)} title]]
        children))

(defn bullet-list
  [items]
  (into [:ul {:class ["list-disc" "space-y-2" "pl-5" "max-w-3xl"]}]
        (map (fn [item]
               [:li {:class elements/p} item])
             items)))

(defn mockup-with-hints
  "A mockup with the explanations of its markers next to it."
  [{:keys [mockup hints]}]
  [:div {:class ["mt-4" "grid" "items-start" "gap-6" "lg:grid-cols-3"]}
   [:div {:class ["lg:col-span-2"]}
    mockup
    [:p {:class ["mt-2" "text-xs" "text-gray-400"]}
     "Schematic illustration, Claude may look slightly different."]]
   [:div {:class ["lg:col-span-1"]}
    (hint/hint-list hints)]])

(defn step
  [i {:keys [title description] :as params}]
  [:div {:class ["mt-8"]}
   [:h3 {:class (into ["flex" "items-center" "gap-3"] elements/h3)}
    [:span {:class ["inline-flex" "h-8" "w-8" "shrink-0" "items-center"
                    "justify-center" "rounded-full" "bg-gray-900" "text-sm"
                    "font-bold" "text-white"]}
     (str (inc i))]
    title]
   (when description
     [:p {:class (into ["mt-2" "max-w-3xl"] elements/p)} description])
   (mockup-with-hints params)])

(defn upload-setting
  "How to allow Claude to upload a file from the chat."
  [state]
  (let [{:keys [description] :as params}
        (guide/upload-setting
          {:host-field (copyable-input state
                                       {:value guide/upload-host
                                        :label "Domain of the Storrito storage"
                                        :store-key :mcp-page/upload-host-copied})})]
    [:div
     [:p {:class (into ["max-w-3xl"] elements/p)} guide/upload-intro]
     [:p {:class (into ["mt-2" "max-w-3xl"] elements/p)} description]
     (mockup-with-hints params)]))

(defn prompt-card
  [prompt]
  [:div {:class ["rounded-lg" "border" "border-gray-200" "bg-white" "px-4"
                 "py-3" "text-sm" "text-gray-800" "shadow-sm"]}
   [:span {:class ["text-gray-400"]} "“"]
   prompt
   [:span {:class ["text-gray-400"]} "”"]])

(defn titled-card
  [{:keys [title description]}]
  [:div {:class ["rounded-lg" "border" "border-gray-200" "bg-white" "p-5"
                 "shadow-sm"]}
   [:h3 {:class ["text-base" "font-semibold" "text-gray-900"]} title]
   [:p {:class (into ["mt-1"] elements/p-sm)} description]])

(defn troubleshooting-entry
  [{:keys [problem solution]}]
  [:div {:class ["border-l-4" "border-yellow-400" "bg-yellow-50" "px-4"
                 "py-3" "max-w-3xl"]}
   [:p {:class ["text-sm" "font-semibold" "text-gray-900"]} problem]
   [:p {:class (into ["mt-1"] elements/p-sm)} solution]])

(defn other-clients
  [state mcp-url]
  [:div {:class ["max-w-3xl"]}
   [:p {:class elements/p}
    guide/other-clients]
   [:div {:class ["mt-3"]}
    (code-with-copy state
                    (guide/claude-code-command mcp-url)
                    :mcp-page/command-copied)]
   [:p {:class (into ["mt-3"] elements/p-sm)}
    "Replace the placeholders with the values shown when you create the credential. "
    [:a {:href "/ui/api-credential/manage/"
         :class ui/a-indigo}
     "Manage API Credentials"]]])

(defn panel
  "The page content. `state` carries the copied flags of the copy
   buttons, `mcp-url` is the organization's MCP server URL."
  [state {:keys [mcp-url]}]
  [:div {:class ["pb-12"]}
   [:h1 {:class ui/h1} "Storrito MCP"]
   [:p {:class ["mt-2" "max-w-3xl" "text-gray-700"]} guide/intro]
   (url-card state mcp-url)
   (section "What you need"
            (bullet-list guide/requirements))
   (apply section "Connect Claude step by step"
          (map-indexed step (guide/steps {:mcp-url mcp-url
                                          :url-field (copyable-input
                                                       state
                                                       {:value mcp-url
                                                        :label "MCP server URL"
                                                        :store-key :mcp-page/mockup-url-copied})})))
   (section "Post a file from the chat"
            (upload-setting state))
   (section "Try it"
            [:p {:class (into ["mb-4" "max-w-3xl"] elements/p)}
             "Attach an image or video to the message when you want Claude to post it. Some things you can ask:"]
            (into [:div {:class ["grid" "gap-3" "sm:grid-cols-2" "max-w-4xl"]}]
                  (map prompt-card guide/example-prompts)))
   (section "What Claude can do with Storrito"
            (into [:div {:class ["grid" "gap-4" "sm:grid-cols-2" "max-w-4xl"]}]
                  (map titled-card guide/capabilities))
            [:p {:class (into ["mt-4"] elements/p-sm)}
             "The tools mirror the procedures of the "
             [:a {:href "/documentation/api/v1/"
                  :target "_blank"
                  :class ui/a-indigo}
              "Storrito API"]
             "."])
   (section "If something does not work"
            (into [:div {:class ["space-y-3"]}]
                  (map troubleshooting-entry guide/troubleshooting)))
   (section "Other MCP clients"
            (other-clients state mcp-url))
   (section "Removing access"
            (into [:div {:class ["grid" "gap-4" "sm:grid-cols-2" "max-w-4xl"]}]
                  (map titled-card guide/remove-access)))])

(defn render
  [state]
  (app-frame/app-frame
    state
    (panel state {:mcp-url (guide/mcp-url js/window.location.origin)})))

(def page
  {:page-id :pages/mcp
   :route [["ui" "mcp"]]
   :render #'render})
